import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Wand2, Loader2, Mic, MicOff, FileDown } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { API, useApp } from "@/state/AppContext";
import { toast } from "sonner";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import { jsPDF } from "jspdf";

export default function ReplyPage() {
  const navigate = useNavigate();
  const { currentResult, language, authHeaders } = useApp();
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState(null);
  const speech = useSpeechRecognition(language === "de" ? "de-DE" : `${language}-${language.toUpperCase()}`);

  useEffect(() => {
    if (speech.transcript) setUserInput(speech.transcript);
  }, [speech.transcript]);

  const generate = async () => {
    if (!userInput.trim()) { toast.error("Bitte beschreibe kurz, was du antworten möchtest."); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/generate-reply`, {
        document_context: currentResult?.analysis || {},
        user_input: userInput,
        tone: "formal",
        target_language: language,
      }, { headers: authHeaders, timeout: 120000 });
      setReply(r.data);
    } catch (e) {
      toast.error("Antwort konnte nicht erstellt werden.");
    } finally { setLoading(false); }
  };

  const copyText = () => {
    if (!reply) return;
    navigator.clipboard.writeText(reply.reply_german);
    toast.success("Antwort kopiert");
  };

  const exportPDF = () => {
    if (!reply) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const marginX = 56; const marginTop = 72; const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const usableW = pageW - marginX * 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(120);
    doc.text(`BriefAssist · ${new Date().toLocaleDateString("de-DE")}`, marginX, 40);
    doc.setTextColor(20);
    doc.setFontSize(12);
    const lines = doc.splitTextToSize(reply.reply_german, usableW);
    let y = marginTop;
    lines.forEach(line => {
      if (y > pageH - 60) { doc.addPage(); y = marginTop; }
      doc.text(line, marginX, y);
      y += 16;
    });
    const sender = (currentResult?.analysis?.sender || "brief").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    doc.save(`antwort-${sender}-${Date.now()}.pdf`);
    toast.success("PDF gespeichert");
  };

  return (
    <div className="px-5 pt-4 pb-8" data-testid="reply-page">
      <button onClick={() => navigate(-1)} data-testid="reply-back" className="text-slate-600 flex items-center gap-1 text-sm mb-3">
        <ArrowLeft className="w-4 h-4" /> Zurück
      </button>
      <h1 className="text-2xl font-bold text-slate-900">Antwort erstellen</h1>
      <p className="text-slate-600 mt-1">Was möchtest du antworten?</p>

      <div className="relative mt-4">
        <Textarea
          data-testid="reply-input"
          value={userInput}
          onChange={e => setUserInput(e.target.value)}
          placeholder="Ich brauche eine Fristverlängerung von zwei Wochen …"
          className="min-h-[120px] rounded-2xl border-slate-200 bg-white p-4 pr-14"
        />
        {speech.supported && (
          <button
            onClick={() => speech.listening ? speech.stop() : speech.start()}
            data-testid="reply-mic-btn"
            aria-label={speech.listening ? "Aufnahme stoppen" : "Spracheingabe"}
            className={`absolute right-3 bottom-3 w-10 h-10 rounded-full flex items-center justify-center ${speech.listening ? "bg-red-500 text-white animate-pulse" : "bg-blue-600 text-white"}`}
          >
            {speech.listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
        )}
      </div>
      {speech.listening && (
        <div className="text-xs text-red-600 mt-1" data-testid="mic-listening">● Höre zu … tippe erneut auf das Mikrofon zum Beenden.</div>
      )}

      <button
        onClick={generate}
        disabled={loading}
        data-testid="reply-generate-btn"
        className="mt-4 w-full min-h-[56px] rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 disabled:bg-slate-300"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
        {loading ? "Antwort wird erstellt …" : "Antwort erstellen"}
      </button>

      {reply && (
        <div className="mt-6 space-y-4">
          <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm" data-testid="reply-german">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Antwort (Deutsch)</div>
            <div className="whitespace-pre-wrap text-slate-800 leading-relaxed">{reply.reply_german}</div>
          </div>
          <div className="rounded-3xl bg-blue-50/50 border border-blue-100 p-4" data-testid="reply-translation">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-2">Was bedeutet diese Antwort?</div>
            <div className="whitespace-pre-wrap text-slate-800 leading-relaxed">{reply.reply_translation}</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <button onClick={copyText} data-testid="reply-copy-btn" className="min-h-[52px] rounded-2xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center gap-2 border border-blue-100">
              <Copy className="w-5 h-5" /> Kopieren
            </button>
            <button onClick={exportPDF} data-testid="reply-pdf-btn" className="min-h-[52px] rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2">
              <FileDown className="w-5 h-5" /> PDF
            </button>
            <button onClick={generate} data-testid="reply-regen-btn" className="min-h-[52px] rounded-2xl bg-white text-slate-700 font-semibold flex items-center justify-center gap-2 border border-slate-200">
              <Wand2 className="w-5 h-5" /> Neu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
